
import { useState, useEffect, useCallback, useRef } from 'react';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { SessionMemoryManager, getMemoryManager } from '@/lib/ai/memory';
import { supabase } from '@/lib/supabase/client';
import type { ChatMessage, SessionMemory } from '@/types/ai';
import { v4 as uuidv4 } from 'uuid';

interface UseAIMemoryReturn {
  memory: SessionMemory | null;
  isLoading: boolean;
  addMessage: (role: 'user' | 'assistant', content: string) => void;
  trackView: (venueId: string) => void;
  trackSave: (venueId: string) => void;
  trackUnsave: (venueId: string) => void;
  updateContext: (update: Partial<SessionMemory['context']>) => void;
  summarize: () => Promise<void>;
  clearMemory: () => void;
  stats: {
    totalMessages: number;
    venuesViewed: number;
    venuesSaved: number;
  };
}

export function useAIMemory(): UseAIMemoryReturn {
  const { user } = useUnifiedAuth();
  const [memory, setMemory] = useState<SessionMemory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const managerRef = useRef<SessionMemoryManager | null>(null);
  // dbSessionIdRef stores ai_sessions.id in the database (uuid)
  const dbSessionIdRef = useRef<string | null>(null);
  // sessionKeyRef stores ai_sessions.session_key (stable string) and is persisted in localStorage
  const sessionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const initMemory = async () => {
      setIsLoading(true);
      try {
        let sessionKey = localStorage.getItem('ai_session_key') || '';
        if (!sessionKey) {
          sessionKey = uuidv4();
          try { localStorage.setItem('ai_session_key', sessionKey); } catch {}
        }
        sessionKeyRef.current = sessionKey;

        const userId = user?.id;

        // Initialize memory manager with session_key (maps to memory.sessionId)
        managerRef.current = getMemoryManager(sessionKey, userId || undefined);

        if (userId) {
          const { data: existingSession, error: findError } = await supabase
            .from('ai_sessions')
            .select('id, context')
            .eq('user_id', userId)
            .eq('session_key', sessionKey)
            .maybeSingle();

          if (!findError && existingSession) {
            dbSessionIdRef.current = existingSession.id as string;
            const stored = (existingSession.context as any) || {};
            if (stored.messages && Array.isArray(stored.messages)) {
              stored.messages.forEach((msg: ChatMessage) => {
                managerRef.current?.addMessage(msg);
              });
            }
            if (stored.context) {
              managerRef.current?.updateContext(stored.context);
            }
          } else {
            // Idempotent create/attach using upsert on session_key
            const { data: created, error: upsertError } = await supabase
              .from('ai_sessions')
              .upsert({
                user_id: userId,
                session_key: sessionKey,
                status: 'active',
                context: { messages: [], context: {}, summary: '' },
                updated_at: new Date().toISOString()
              }, { onConflict: 'user_id,session_key' })
              .select('id')
              .maybeSingle();

            if (!upsertError && created?.id) {
              dbSessionIdRef.current = created.id as string;
            }
          }
        }

        setMemory(managerRef.current.getMemory());
      } catch (error) {
        console.error('[useAIMemory] Failed to initialize:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initMemory();
  }, [user]);

  // Persist to Supabase using session_key upsert (idempotent)
  const persistToSupabase = useCallback(async () => {
    if (!user?.id || !managerRef.current || !sessionKeyRef.current) return;

    const currentMemory = managerRef.current.getMemory();

    try {
      await supabase
        .from('ai_sessions')
        .upsert({
          user_id: user.id,
          session_key: sessionKeyRef.current,
          status: 'active',
          context: {
            messages: currentMemory.messages.slice(-20),
            context: currentMemory.context,
            summary: currentMemory.summary
          },
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id,session_key' });
    } catch (error) {
      console.error('[useAIMemory] Failed to persist:', error);
    }
  }, [user]);

  const addMessage = useCallback((role: 'user' | 'assistant', content: string) => {
    if (!managerRef.current) return;

    const message: ChatMessage = {
      id: uuidv4(),
      role,
      content,
      timestamp: Date.now()
    };

    managerRef.current.addMessage(message);
    setMemory(managerRef.current.getMemory());

    persistToSupabase();
  }, [persistToSupabase]);

  const trackView = useCallback((venueId: string) => {
    if (!managerRef.current) return;

    managerRef.current.trackVenue(venueId, 'view');
    setMemory(managerRef.current.getMemory());
  }, []);

  const trackSave = useCallback((venueId: string) => {
    if (!managerRef.current) return;

    managerRef.current.trackVenue(venueId, 'save');
    setMemory(managerRef.current.getMemory());
    persistToSupabase();
  }, [persistToSupabase]);

  const trackUnsave = useCallback((venueId: string) => {
    if (!managerRef.current) return;

    managerRef.current.trackVenue(venueId, 'unsave');
    setMemory(managerRef.current.getMemory());
    persistToSupabase();
  }, [persistToSupabase]);

  const updateContext = useCallback((update: Partial<SessionMemory['context']>) => {
    if (!managerRef.current) return;

    const currentMemory = managerRef.current.getMemory();
    let hasChanges = false;

    for (const key in update) {
      if (JSON.stringify((currentMemory.context as any)[key]) !== JSON.stringify((update as any)[key])) {
        hasChanges = true;
        break;
      }
    }

    if (hasChanges) {
      managerRef.current.updateContext(update);
      setMemory(managerRef.current.getMemory());
    }
  }, []);

  // Refresh summary from ai_sessions (remy-ai automatically generates summaries every 4 turns)
  const summarize = useCallback(async () => {
    if (!user?.id || !sessionKeyRef.current || !managerRef.current) return;

    try {
      const { data: session, error } = await supabase
        .from('ai_sessions')
        .select('context')
        .eq('user_id', user.id)
        .eq('session_key', sessionKeyRef.current)
        .maybeSingle();

      if (!error && session?.context) {
        const context = session.context as any;
        const summary = context.summary || '';
        
        const updatedMemory = managerRef.current.getMemory();
        updatedMemory.summary = summary;
        updatedMemory.lastSummarizedAt = Date.now();
        updatedMemory.messagesSinceSummary = 0;
        setMemory(updatedMemory);
      }
    } catch (error) {
      console.error('[useAIMemory] Summary refresh failed:', error);
    }
  }, [user, persistToSupabase]);

  // Clear memory (preserves session_key but clears content)
  const clearMemory = useCallback(() => {
    if (!managerRef.current) return;

    managerRef.current.clear();
    setMemory(managerRef.current.getMemory());
    persistToSupabase();
  }, [persistToSupabase]);

  const stats = memory ? {
    totalMessages: memory.messages.length,
    venuesViewed: memory.context.viewedVenues.length,
    venuesSaved: memory.context.savedVenues.length
  } : {
    totalMessages: 0,
    venuesViewed: 0,
    venuesSaved: 0
  };

  return {
    memory,
    isLoading,
    addMessage,
    trackView,
    trackSave,
    trackUnsave,
    updateContext,
    summarize,
    clearMemory,
    stats
  };
}